import { useMemo, useState } from "react";
import { ListChecks, SlidersHorizontal } from "lucide-react";
import type { AbrangenciaDoServico } from "@bookdental/schemas";
import { IconeColorido } from "../selecao/IconeColorido";
import { iconeDaCategoria } from "../selecao/icones-da-selecao";
import { CartaoDeItem } from "../selecao/CartaoDeItem";
import { ConfigurarServicoDialog } from "./ConfigurarServicoDialog";
import { ConfigurarGrupoDialog, type ResultadoDoGrupo } from "./ConfigurarGrupoDialog";
import { ROTULO_DA_ABRANGENCIA, nomeDoMaterial, resumoDosMateriais, type EscalaDaBase, type MaterialDaBase, type MaterialProprio, type ServicoEscolhido } from "./tipos";

/**
 * Passo 2: os serviços escolhidos, um cartão cada, por categoria. O cartão já mostra os materiais (o
 * sugerido pela base passa direto); clicar abre o serviço pra mudar materiais, criar um novo e dizer
 * as escalas de cor de cada material. Abriu e saiu, o cartão fica verde: já foi visto. Vários de uma
 * vez (pedido do Jun): "Configurar a categoria" ou "Selecionar vários".
 */
export function PassoConfigurar({
  servicos,
  mapa,
  padraoDe,
  materiais,
  proprios,
  onNovoProprio,
  escalas,
  escalasPorMaterial,
  abrangenciaDe,
  revisados,
  onRevisado,
  onSalvarServico,
  onSalvarGrupo,
}: {
  servicos: ServicoEscolhido[];
  /** Serviço → materiais, já final (configurado ou o padrão da base). */
  mapa: Record<string, string[]>;
  padraoDe: (chave: string) => string[];
  materiais: MaterialDaBase[];
  proprios: MaterialProprio[];
  onNovoProprio: (nome: string, familia?: string) => void;
  escalas: EscalaDaBase[];
  escalasPorMaterial: Record<string, string[]>;
  abrangenciaDe: (chave: string) => AbrangenciaDoServico;
  revisados: string[];
  /** Fechou o serviço sem salvar: conta como visto. */
  onRevisado: (chave: string) => void;
  /** Salvar também marca o serviço como visto. */
  onSalvarServico: (chave: string, materiais: string[], escalasPorMaterial: Record<string, string[]>, abrangencia: AbrangenciaDoServico) => void;
  /** Configurou vários de uma vez — todos contam como vistos. */
  onSalvarGrupo: (chaves: string[], resultado: ResultadoDoGrupo) => void;
}) {
  const [aberto, setAberto] = useState<ServicoEscolhido | null>(null);
  const [grupo, setGrupo] = useState<ServicoEscolhido[] | null>(null);
  // Modo "Selecionar vários": o clique marca em vez de abrir.
  const [selecionando, setSelecionando] = useState(false);
  const [selecao, setSelecao] = useState<string[]>([]);
  const alternarSelecao = (chave: string) => setSelecao((atual) => (atual.includes(chave) ? atual.filter((c) => c !== chave) : [...atual, chave]));
  const sairDaSelecao = () => {
    setSelecionando(false);
    setSelecao([]);
  };
  const daBase = useMemo(() => new Map(materiais.map((m) => [m.id, m])), [materiais]);
  const vistos = useMemo(() => new Set(revisados), [revisados]);
  const categorias = [...new Set(servicos.map((s) => s.categoria))];
  const nomes = (chave: string) => (mapa[chave] ?? []).map((m) => nomeDoMaterial(m, daBase));
  const nRevisados = servicos.filter((s) => vistos.has(s.chave)).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-foreground">Em que materiais você faz cada serviço?</h2>
          <p className="text-sm text-zinc-500 dark:text-muted-foreground">
            Já vem o que costuma ser usado em cada trabalho. Toque no serviço pra conferir se o dentista marca dente ou arcada, os
            materiais e as escalas de cor. Depois de visto, ele fica verde.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <p className="text-xs font-medium tabular-nums text-zinc-500 dark:text-muted-foreground">
            <span className="font-semibold text-emerald-600">{nRevisados}</span> de {servicos.length} revisados
          </p>
          <button
            type="button"
            onClick={() => (selecionando ? sairDaSelecao() : setSelecionando(true))}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground"
          >
            <ListChecks className="h-3.5 w-3.5" />
            {selecionando ? "Cancelar seleção" : "Selecionar vários"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {categorias.map((c) => {
          const daCategoria = servicos.filter((s) => s.categoria === c);
          return (
            <section key={c} className="rounded-xl border border-zinc-200 bg-white p-3 shadow-sm dark:border-border dark:bg-card">
              <div className="mb-2.5 flex items-center gap-2.5">
                <IconeColorido {...iconeDaCategoria(c)} tamanho="sm" />
                <p className="min-w-0 flex-1 text-[15px] font-semibold text-zinc-900 dark:text-foreground">
                  {c} <span className="text-[11.5px] font-normal text-zinc-500 dark:text-muted-foreground">· {daCategoria.length} {daCategoria.length === 1 ? "serviço" : "serviços"}</span>
                </p>
                {selecionando ? (
                  <button
                    type="button"
                    onClick={() => {
                      const todos = daCategoria.every((s) => selecao.includes(s.chave));
                      const daqui = daCategoria.map((s) => s.chave);
                      setSelecao((atual) => (todos ? atual.filter((x) => !daqui.includes(x)) : [...new Set([...atual, ...daqui])]));
                    }}
                    className="shrink-0 text-[12px] font-semibold text-brand-700 hover:underline dark:text-primary"
                  >
                    {daCategoria.every((s) => selecao.includes(s.chave)) ? "Desmarcar todos" : "Marcar todos"}
                  </button>
                ) : (
                  daCategoria.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setGrupo(daCategoria)}
                      className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-3 text-xs font-semibold text-brand-700 hover:bg-brand-100 dark:border-primary/30 dark:bg-primary/10 dark:text-primary"
                    >
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                      Configurar a categoria
                    </button>
                  )
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {daCategoria.map((s) => (
                  <CartaoDeItem
                    key={s.chave}
                    titulo={s.proprio ? `${s.nome} (seu)` : s.nome}
                    detalhe={`${ROTULO_DA_ABRANGENCIA[abrangenciaDe(s.chave)]} · ${resumoDosMateriais(nomes(s.chave))}`}
                    estado={selecionando ? (selecao.includes(s.chave) ? "marcado" : "normal") : vistos.has(s.chave) ? "revisado" : "normal"}
                    alternavel={selecionando}
                    onClick={() => (selecionando ? alternarSelecao(s.chave) : setAberto(s))}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {selecionando && (
        <div className="sticky bottom-4 z-20 flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-lg dark:border-border dark:bg-card">
          <span className="text-sm text-zinc-600 dark:text-muted-foreground">
            {selecao.length === 0 ? "Toque nos serviços para selecionar." : `${selecao.length} ${selecao.length === 1 ? "selecionado" : "selecionados"}`}
          </span>
          <button
            type="button"
            disabled={selecao.length === 0}
            onClick={() => setGrupo(servicos.filter((s) => selecao.includes(s.chave)))}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Configurar selecionados
          </button>
        </div>
      )}

      <ConfigurarGrupoDialog
        servicos={grupo}
        onFechar={() => setGrupo(null)}
        mapa={mapa}
        padraoDe={padraoDe}
        abrangenciaDe={abrangenciaDe}
        materiais={materiais}
        proprios={proprios}
        onNovoProprio={onNovoProprio}
        escalas={escalas}
        escalasPorMaterial={escalasPorMaterial}
        onSalvar={(chaves, resultado) => {
          onSalvarGrupo(chaves, resultado);
          setGrupo(null);
          sairDaSelecao();
        }}
      />

      <ConfigurarServicoDialog
        servico={aberto}
        onFechar={() => {
          if (aberto) onRevisado(aberto.chave);
          setAberto(null);
        }}
        padrao={aberto ? padraoDe(aberto.chave) : []}
        atuais={aberto ? (mapa[aberto.chave] ?? []) : []}
        materiais={materiais}
        proprios={proprios}
        onNovoProprio={onNovoProprio}
        escalas={escalas}
        escalasPorMaterial={escalasPorMaterial}
        abrangencia={aberto ? abrangenciaDe(aberto.chave) : "DENTE"}
        onSalvar={(ms, es, ab) => {
          if (aberto) onSalvarServico(aberto.chave, ms, es, ab);
          setAberto(null);
        }}
      />
    </div>
  );
}
